import React, { useState, useEffect } from "react";
import {
  X,
  Search,
  UserPlus,
  Calculator,
  Settings,
  Gift,
  ChevronRight,
  Check,
  Building,
  Plus,
  Trash2,
} from "lucide-react";
import {
  commissionService,
  CreateAgentPlayerAssignment,
  PackageType,
} from "../../api/services/commission/commissionService";
import { customerService } from "../../api/services/customers/customerService";
import { Customer, CreateCustomerRequest } from "../../types/customer";
import { Country } from "../../api/services/countries/type";
import { CustomerForm } from "../customers/CustomerForm";
import { facilitiesService } from "../../api/services/facilities/facilitiesService";
import { FacilityCategory } from "../../api/services/facilities/type";
import toast from "react-hot-toast";

interface FacilityItem {
  id: string;
  categoryId: string;
  categoryName?: string;
  facilityName: string;
  description: string;
  estimatedCost?: number;
  actualCost: number;
  vendorName?: string;
  conditions?: string;
}

interface AdvancedPlayerAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  agentId: string;
}

export const AdvancedPlayerAssignmentModal: React.FC<
  AdvancedPlayerAssignmentModalProps
> = ({ isOpen, onClose, onSuccess, agentId }) => {
  const [activeTab, setActiveTab] = useState<
    "basic" | "commission" | "facilities"
  >("basic");
  const [formData, setFormData] = useState<CreateAgentPlayerAssignment>({
    agentId,
    playerId: "",
    packageTypeId: "",
    agentType: "NORMAL",
    maxBetLimit: 0,
    startDate: new Date().toISOString().split("T")[0],
    facilitiesAgreed: false,
  });
  const [errors, setErrors] = useState<{
    playerId?: string;
    packageTypeId?: string;
    maxBetLimit?: string;
  }>({});

  // Player search and management
  const [players, setPlayers] = useState<Customer[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredPlayers, setFilteredPlayers] = useState<Customer[]>([]);
  const [showPlayerDropdown, setShowPlayerDropdown] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<Customer | null>(null);
  const [showCreatePlayer, setShowCreatePlayer] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);

  // Packages
  const [packages, setPackages] = useState<PackageType[]>([]);
  const [loading, setLoading] = useState(false);

  // Commission settings
  const [useSimpleCommission, setUseSimpleCommission] = useState(true);

  // Facilities
  const [facilities, setFacilities] = useState<FacilityItem[]>([]);
  const [facilityCategories, setFacilityCategories] = useState<
    FacilityCategory[]
  >([]);
  const [facilitiesCondition, setFacilitiesCondition] = useState<
    "ON_LOSS" | "GUARANTEED" | "CONDITIONAL"
  >("GUARANTEED");
  const [totalLossThreshold, setTotalLossThreshold] = useState<number>(0);

  useEffect(() => {
    if (isOpen) {
      fetchPlayers();
      fetchPackages();
      fetchCountries();
      fetchFacilityCategories();
    }
  }, [isOpen]);

  // Filter players based on search
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredPlayers(players);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = players.filter(
        (player) =>
          player.name.toLowerCase().includes(query) ||
          player.playerId?.toLowerCase().includes(query) ||
          player.phone?.toLowerCase().includes(query),
      );
      setFilteredPlayers(filtered);
    }
  }, [searchQuery, players]);

  const fetchPlayers = async () => {
    try {
      const response = await customerService.getCustomers({ limit: 100 });
      setPlayers(response.items || []);
      setFilteredPlayers(response.items || []);
    } catch (error) {
      console.error("Error fetching players:", error);
      toast.error("Failed to fetch players");
    }
  };

  const fetchPackages = async () => {
    try {
      const packageTypes = await commissionService.getPackageTypes();
      setPackages(packageTypes);
      if (packageTypes.length > 0) {
        const normalPackage =
          packageTypes.find((p) => p.name === "Normal") || packageTypes[0];
        setFormData((prev) => ({ ...prev, packageTypeId: normalPackage.id }));
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
      toast.error("Failed to fetch package types");
    }
  };

  const fetchCountries = async () => {
    try {
      const countriesData = await customerService.getCountries();
      setCountries(countriesData);
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  };

  const fetchFacilityCategories = async () => {
    try {
      const response = await facilitiesService.getCategories({ limit: 100 });
      setFacilityCategories(response.items || []);
    } catch (error) {
      console.error("Error fetching facility categories:", error);
    }
  };

  const handlePlayerSelect = (player: Customer) => {
    setSelectedPlayer(player);
    setFormData((prev) => ({ ...prev, playerId: player.id }));
    setSearchQuery(player.name);
    setShowPlayerDropdown(false);
  };

  const handleCreatePlayer = async (data: CreateCustomerRequest) => {
    try {
      setLoading(true);
      // Create player via customer service
      const newPlayer = await customerService.createCustomer(data);

      // Add to players list and select
      setPlayers((prev) => [...prev, newPlayer]);
      handlePlayerSelect(newPlayer);
      setShowCreatePlayer(false);
      toast.success("Player created successfully");
      // Refresh the players list
      if (searchQuery) {
        const filtered = players.filter(
          (player) =>
            player.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            player.playerId?.toLowerCase().includes(searchQuery.toLowerCase()),
        );
        setFilteredPlayers(filtered);
      }
    } catch (error) {
      console.error("Error creating player:", error);
      toast.error("Failed to create player");
    } finally {
      setLoading(false);
    }
  };

  const addFacility = () => {
    if (facilityCategories.length === 0) {
      toast.error("Please create facility categories first");
      return;
    }
    const defaultCategory = facilityCategories[0];
    setFacilities((prev) => [
      ...prev,
      {
        id: `facility-${Date.now()}`,
        categoryId: defaultCategory?.id || "",
        categoryName: defaultCategory?.name || "",
        facilityName: "",
        description: "",
        estimatedCost: 0,
        actualCost: 0,
        vendorName: "",
      },
    ]);
  };

  const updateFacility = (id: string, updates: Partial<FacilityItem>) => {
    setFacilities((prev) =>
      prev.map((facility) =>
        facility.id === id ? { ...facility, ...updates } : facility,
      ),
    );
  };

  const removeFacility = (id: string) => {
    setFacilities((prev) => prev.filter((facility) => facility.id !== id));
  };

  const validateBasicInfo = () => {
    const newErrors: typeof errors = {};

    if (!formData.playerId) {
      newErrors.playerId = "Please select a player";
    }
    if (!formData.packageTypeId) {
      newErrors.packageTypeId = "Please select a package type";
    }
    if (!formData.maxBetLimit || formData.maxBetLimit <= 0) {
      newErrors.maxBetLimit = "Please enter a valid max bet limit";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateBasicInfo()) return;

    setLoading(true);
    try {
      // Prepare assignment data
      const data: CreateAgentPlayerAssignment = {
        ...formData,
        maxBetLimit: Number(formData.maxBetLimit),
        facilitiesAgreed: formData.facilitiesAgreed,
      };

      // Add commission configuration based on type
      if (useSimpleCommission) {
        data.commissionPercentage = formData.commissionPercentage;
        if (formData.agentType === "JUNKET") {
          data.sharePercentage = formData.sharePercentage;
        }
      } else {
        data.lossCommissionSplit = formData.lossCommissionSplit;
        data.rollingCommissionSplit = formData.rollingCommissionSplit;
        data.depositCommissionSplit = formData.depositCommissionSplit;
      }

      // Create the assignment first
      const assignment = await commissionService.createAssignment(data);

      // If facilities are agreed and there are facilities to create
      if (formData.facilitiesAgreed && facilities.length > 0) {
        try {
          // Create each facility via API
          const facilityPromises = facilities.map((facility) =>
            facilitiesService.createPlayerFacility({
              playerId: formData.playerId,
              assignmentId: assignment.id,
              facilityCategoryId: facility.categoryId,
              facilityName: facility.facilityName,
              description: facility.description,
              estimatedCost: facility.estimatedCost,
              actualCost: facility.actualCost,
              providedDate: new Date().toISOString().split("T")[0],
              vendorName: facility.vendorName,
              notes: facility.conditions,
            }),
          );

          await Promise.all(facilityPromises);
          toast.success(
            "Player assignment and facilities created successfully",
          );
        } catch (facilityError) {
          console.error("Error creating facilities:", facilityError);
          toast.error("Assignment created but some facilities failed to save");
        }
      } else {
        toast.success("Player assignment created successfully");
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error creating assignment:", error);
      toast.error("Failed to create assignment");
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    {
      id: "basic",
      label: "Basic Info",
      icon: <Settings className="w-4 h-4" />,
    },
    {
      id: "commission",
      label: "Commission",
      icon: <Calculator className="w-4 h-4" />,
    },
    ...(formData.facilitiesAgreed
      ? [
          {
            id: "facilities",
            label: "Facilities",
            icon: <Gift className="w-4 h-4" />,
          },
        ]
      : []),
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-slate-800 rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white">
                Create Player Assignment
              </h2>
              <p className="text-slate-400 text-sm mt-1">
                Assign a player to agent with commission structure
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-700">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() =>
                setActiveTab(tab.id as "basic" | "commission" | "facilities")
              }
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-all relative ${
                activeTab === tab.id
                  ? "text-blue-400 border-b-2 border-blue-400"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Basic Info Tab */}
          {activeTab === "basic" && (
            <div className="space-y-6">
              {/* Player Selection with Search */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Select Player *
                </label>

                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setShowPlayerDropdown(true);
                      }}
                      onFocus={() => {
                        setShowPlayerDropdown(true);
                        setErrors((prev) => ({ ...prev, playerId: undefined }));
                      }}
                      placeholder="Search player by name, ID, or phone..."
                      className={`w-full pl-10 pr-4 py-3 bg-slate-700 border rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.playerId ? "border-red-500" : "border-slate-600"
                      }`}
                    />
                  </div>

                  {/* Dropdown */}
                  {showPlayerDropdown && (
                    <div className="absolute top-full mt-2 w-full bg-slate-700 border border-slate-600 rounded-lg shadow-xl max-h-60 overflow-y-auto z-10">
                      {filteredPlayers.length > 0 ? (
                        filteredPlayers.map((player) => (
                          <button
                            key={player.id}
                            onClick={() => handlePlayerSelect(player)}
                            className="w-full px-4 py-3 text-left hover:bg-slate-600 transition-colors flex items-center justify-between group"
                          >
                            <div>
                              <p className="text-white font-medium">
                                {player.name}
                              </p>
                              <p className="text-slate-400 text-sm">
                                ID: {player.playerId} | Phone: {player.phone}
                              </p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-white" />
                          </button>
                        ))
                      ) : (
                        <div className="p-4 text-center">
                          <p className="text-slate-400 mb-3">
                            No players found
                          </p>
                          <button
                            onClick={() => {
                              setShowPlayerDropdown(false);
                              setShowCreatePlayer(true);
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 mx-auto"
                          >
                            <UserPlus className="w-4 h-4" />
                            Create New Player
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {errors.playerId && (
                  <p className="text-red-400 text-sm mt-1">{errors.playerId}</p>
                )}

                {selectedPlayer && (
                  <div className="mt-4 p-4 bg-slate-700/50 rounded-lg border border-slate-600">
                    <p className="text-sm text-slate-400 mb-1">
                      Selected Player
                    </p>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">
                          {selectedPlayer.name}
                        </p>
                        <p className="text-slate-400 text-sm">
                          ID: {selectedPlayer.playerId} | Phone:{" "}
                          {selectedPlayer.phone}
                        </p>
                      </div>
                      <Check className="w-5 h-5 text-green-400" />
                    </div>
                  </div>
                )}
              </div>

              {/* Package and Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Package Type *
                  </label>
                  <select
                    value={formData.packageTypeId}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        packageTypeId: e.target.value,
                      }));
                      setErrors((prev) => ({
                        ...prev,
                        packageTypeId: undefined,
                      }));
                    }}
                    className={`w-full p-3 bg-slate-700 border rounded-lg text-white focus:ring-2 focus:ring-blue-500 ${
                      errors.packageTypeId
                        ? "border-red-500"
                        : "border-slate-600"
                    }`}
                    required
                  >
                    <option value="">Select package...</option>
                    {packages.map((pkg) => (
                      <option key={pkg.id} value={pkg.id}>
                        {pkg.name} (₹{pkg.betAmountPerPoint} per point)
                      </option>
                    ))}
                  </select>
                  {errors.packageTypeId && (
                    <p className="text-red-400 text-sm mt-1">
                      {errors.packageTypeId}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Agent Type *
                  </label>
                  <select
                    value={formData.agentType}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        agentType: e.target.value as "NORMAL" | "JUNKET",
                      }))
                    }
                    className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="NORMAL">Normal (Commission Only)</option>
                    <option value="JUNKET">Junket (Commission + Share)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Maximum Bet Limit *
                  </label>
                  <input
                    type="number"
                    value={formData.maxBetLimit}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        maxBetLimit: Number(e.target.value),
                      }));
                      setErrors((prev) => ({
                        ...prev,
                        maxBetLimit: undefined,
                      }));
                    }}
                    className={`w-full p-3 bg-slate-700 border rounded-lg text-white focus:ring-2 focus:ring-blue-500 ${
                      errors.maxBetLimit ? "border-red-500" : "border-slate-600"
                    }`}
                    placeholder="Enter max bet limit"
                    required
                  />
                  {errors.maxBetLimit && (
                    <p className="text-red-400 text-sm mt-1">
                      {errors.maxBetLimit}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        startDate: e.target.value,
                      }))
                    }
                    className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              {/* Facilities Agreement Checkbox */}
              <div className="mt-6 bg-slate-700/50 rounded-lg p-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.facilitiesAgreed}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        facilitiesAgreed: e.target.checked,
                      }));
                      // Reset facilities if unchecked
                      if (!e.target.checked) {
                        setFacilities([]);
                      }
                    }}
                    className="w-4 h-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500"
                  />
                  <div>
                    <span className="text-white font-medium">
                      Enable Facilities Agreement
                    </span>
                    <p className="text-slate-400 text-sm mt-1">
                      Check this to add facility agreements for the player
                    </p>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Commission Tab */}
          {activeTab === "commission" && (
            <div className="space-y-6">
              {/* Commission Type Toggle */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Commission Type
                </label>
                <div className="flex gap-4 p-4 bg-slate-700/50 rounded-lg">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      checked={useSimpleCommission}
                      onChange={() => setUseSimpleCommission(true)}
                      className="mr-2"
                    />
                    <span className="text-white">Simple Percentage</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      checked={!useSimpleCommission}
                      onChange={() => setUseSimpleCommission(false)}
                      className="mr-2"
                    />
                    <span className="text-white">Detailed Splits</span>
                  </label>
                </div>
              </div>

              {useSimpleCommission ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Commission Percentage (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.commissionPercentage || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          commissionPercentage: e.target.value
                            ? Number(e.target.value)
                            : undefined,
                        }))
                      }
                      className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                      placeholder="2.0"
                    />
                  </div>

                  {formData.agentType === "JUNKET" && (
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Share Percentage (%)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={formData.sharePercentage || ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            sharePercentage: e.target.value
                              ? Number(e.target.value)
                              : undefined,
                          }))
                        }
                        className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                        placeholder="25.0"
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Agent Commission Settings */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        General Agent Commission (%)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={formData.commissionPercentage || ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            commissionPercentage: e.target.value
                              ? Number(e.target.value)
                              : undefined,
                          }))
                        }
                        className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                        placeholder="2.0"
                      />
                      <p className="text-slate-500 text-xs mt-1">
                        General commission rate for overall business operations
                      </p>
                    </div>

                    {formData.agentType === "JUNKET" && (
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Agent Share Percentage (%)
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          value={formData.sharePercentage || ""}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              sharePercentage: e.target.value
                                ? Number(e.target.value)
                                : undefined,
                            }))
                          }
                          className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                          placeholder="25.0"
                        />
                        <p className="text-slate-500 text-xs mt-1">
                          Percentage of total revenue shared with the agent
                          (typically 10-40%)
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Deposit Commission Split */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-3">
                      Deposit Commission Split (%)
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">
                          Agent
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          value={formData.depositCommissionSplit?.agent || ""}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              depositCommissionSplit: {
                                agent: e.target.value
                                  ? Number(e.target.value)
                                  : 0,
                                player:
                                  prev.depositCommissionSplit?.player || 0,
                              },
                            }))
                          }
                          className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                          placeholder="2.0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">
                          Player
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          value={formData.depositCommissionSplit?.player || ""}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              depositCommissionSplit: {
                                agent: prev.depositCommissionSplit?.agent || 0,
                                player: e.target.value
                                  ? Number(e.target.value)
                                  : 0,
                              },
                            }))
                          }
                          className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                          placeholder="1.0"
                        />
                      </div>
                    </div>
                    <p className="text-slate-500 text-xs mt-2">
                      Commission split for cash deposits
                    </p>
                  </div>

                  {/* Rolling Commission Split */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-3">
                      Rolling Commission Split (%)
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">
                          Agent
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          value={formData.rollingCommissionSplit?.agent || ""}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              rollingCommissionSplit: {
                                agent: e.target.value
                                  ? Number(e.target.value)
                                  : 0,
                                player:
                                  prev.rollingCommissionSplit?.player || 0,
                              },
                            }))
                          }
                          className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                          placeholder="0.8"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">
                          Player
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          value={formData.rollingCommissionSplit?.player || ""}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              rollingCommissionSplit: {
                                agent: prev.rollingCommissionSplit?.agent || 0,
                                player: e.target.value
                                  ? Number(e.target.value)
                                  : 0,
                              },
                            }))
                          }
                          className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                          placeholder="0.2"
                        />
                      </div>
                    </div>
                    <p className="text-slate-500 text-xs mt-2">
                      Commission split for total bet amount (turnover)
                    </p>
                  </div>

                  {/* Loss Commission Split */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-3">
                      Loss Commission Split (%)
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">
                          Agent
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          value={formData.lossCommissionSplit?.agent || ""}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              lossCommissionSplit: {
                                agent: e.target.value
                                  ? Number(e.target.value)
                                  : 0,
                                player: prev.lossCommissionSplit?.player || 0,
                              },
                            }))
                          }
                          className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                          placeholder="15.0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">
                          Player
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          value={formData.lossCommissionSplit?.player || ""}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              lossCommissionSplit: {
                                agent: prev.lossCommissionSplit?.agent || 0,
                                player: e.target.value
                                  ? Number(e.target.value)
                                  : 0,
                              },
                            }))
                          }
                          className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                          placeholder="5.0"
                        />
                      </div>
                    </div>
                    <p className="text-slate-500 text-xs mt-2">
                      Commission split on net loss (applies when player wins)
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Facilities Tab */}
          {activeTab === "facilities" && (
            <div className="space-y-6">
              {/* Facilities Agreement */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-slate-300">
                    Facilities Agreement
                  </h3>
                  <button
                    onClick={addFacility}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-sm flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Facility
                  </button>
                </div>

                {/* Facility Condition */}
                {facilities.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Facility Condition
                      </label>
                      <select
                        value={facilitiesCondition}
                        onChange={(e) =>
                          setFacilitiesCondition(
                            e.target.value as
                              | "ON_LOSS"
                              | "GUARANTEED"
                              | "CONDITIONAL",
                          )
                        }
                        className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="GUARANTEED">
                          Guaranteed - Always provided
                        </option>
                        <option value="ON_LOSS">
                          On Loss - Provided when player loses threshold amount
                        </option>
                        <option value="CONDITIONAL">
                          Conditional - Based on specific conditions
                        </option>
                      </select>
                    </div>

                    {facilitiesCondition === "ON_LOSS" && (
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Loss Threshold Amount
                        </label>
                        <input
                          type="number"
                          value={totalLossThreshold}
                          onChange={(e) =>
                            setTotalLossThreshold(Number(e.target.value))
                          }
                          className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                          placeholder="Minimum loss amount for facilities"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Facilities List */}
                <div className="space-y-3">
                  {facilities.length === 0 ? (
                    <div className="text-center py-8">
                      <Gift className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                      <p className="text-slate-400">No facilities added yet</p>
                      <p className="text-slate-500 text-sm">
                        Add facilities like accommodation, food, transport, etc.
                      </p>
                    </div>
                  ) : (
                    facilities.map((facility) => (
                      <div
                        key={facility.id}
                        className="bg-slate-700/50 rounded-lg p-4 border border-slate-600"
                      >
                        <div className="flex items-start gap-4">
                          <div className="mt-1">
                            <Building className="w-4 h-4" />
                          </div>
                          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                            <select
                              value={facility.categoryId}
                              onChange={(e) => {
                                const category = facilityCategories.find(
                                  (c) => c.id === e.target.value,
                                );
                                updateFacility(facility.id, {
                                  categoryId: e.target.value,
                                  categoryName: category?.name || "",
                                });
                              }}
                              className="p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Select Category</option>
                              {facilityCategories.map((category) => (
                                <option key={category.id} value={category.id}>
                                  {category.name}
                                </option>
                              ))}
                            </select>
                            <input
                              type="text"
                              value={facility.facilityName}
                              onChange={(e) =>
                                updateFacility(facility.id, {
                                  facilityName: e.target.value,
                                })
                              }
                              className="p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                              placeholder="Facility Name"
                            />
                            <input
                              type="number"
                              value={facility.actualCost || ""}
                              onChange={(e) =>
                                updateFacility(facility.id, {
                                  actualCost: Number(e.target.value),
                                })
                              }
                              className="p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                              placeholder="Cost Amount"
                            />
                          </div>
                          <button
                            onClick={() => removeFacility(facility.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <input
                            type="text"
                            value={facility.vendorName || ""}
                            onChange={(e) =>
                              updateFacility(facility.id, {
                                vendorName: e.target.value,
                              })
                            }
                            className="p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                            placeholder="Vendor Name (optional)"
                          />
                          <input
                            type="number"
                            value={facility.estimatedCost || ""}
                            onChange={(e) =>
                              updateFacility(facility.id, {
                                estimatedCost: Number(e.target.value),
                              })
                            }
                            className="p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                            placeholder="Estimated Cost (optional)"
                          />
                        </div>
                        <textarea
                          value={facility.description || ""}
                          onChange={(e) =>
                            updateFacility(facility.id, {
                              description: e.target.value,
                            })
                          }
                          rows={2}
                          className="mt-3 w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 resize-none"
                          placeholder="Description (optional)"
                        />
                        <input
                          type="text"
                          value={facility.conditions || ""}
                          onChange={(e) =>
                            updateFacility(facility.id, {
                              conditions: e.target.value,
                            })
                          }
                          className="mt-3 w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                          placeholder="Specific conditions (optional)"
                        />
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-700 p-6">
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-slate-600 text-slate-300 hover:bg-slate-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={
                loading ||
                !formData.playerId ||
                !formData.packageTypeId ||
                !formData.maxBetLimit
              }
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Creating..." : "Create Assignment"}
            </button>
          </div>
        </div>
      </div>

      {/* Create Player Modal - Using existing CustomerForm */}
      {showCreatePlayer && (
        <CustomerForm
          customer={undefined}
          countries={countries}
          onSubmit={(data) => {
            if ("playerId" in data && "customerType" in data) {
              handleCreatePlayer(data as CreateCustomerRequest);
            }
          }}
          onCancel={() => setShowCreatePlayer(false)}
          isLoading={loading}
        />
      )}
    </div>
  );
};
